/**
 * Centralized Logging Utility for Trillboard Screen
 * Provides structured logging with levels, deduplication, and environment-based filtering
 */

class Logger {
  constructor() {
    this.logLevels = {
      ERROR: 0,
      WARN: 1,
      INFO: 2,
      DEBUG: 3,
      TRACE: 4
    };

    // Set log level based on environment
    this.currentLevel = process.env.NODE_ENV === 'development' 
      ? this.logLevels.DEBUG 
      : this.logLevels.WARN;

    // Log deduplication cache
    this.logCache = new Map();
    this.cacheTimeout = 5000; // 5 seconds
    this.maxLogCacheSize = 500; // MEMORY FIX: Cap to prevent unbounded growth

    // Performance monitoring
    this.performanceLogs = new Map();
    this.maxPerformanceLogKeys = 100; // MEMORY FIX: Cap to prevent unbounded growth
    this.batchSize = 10;
    this.batchTimeout = 1000; // 1 second

    // Initialize performance monitoring
    this.initPerformanceMonitoring();
  }

  /**
   * Initialize performance monitoring for batch logging
   */
  initPerformanceMonitoring() {
    if (this.currentLevel >= this.logLevels.DEBUG) {
      setInterval(() => {
        this.flushPerformanceLogs();
        this.cleanupCache(); // MEMORY FIX: Auto-cleanup expired dedup entries
      }, this.batchTimeout);
    }
  }

  /**
   * Check if a log should be displayed based on current level
   */
  shouldLog(level) {
    return level <= this.currentLevel;
  }

  /**
   * Generate a cache key for deduplication
   */
  generateCacheKey(level, message, data) {
    const dataHash = data ? JSON.stringify(data).slice(0, 100) : '';
    return `${level}:${message}:${dataHash}`;
  }

  /**
   * Check if log should be deduplicated
   */
  shouldDeduplicate(level, message, data) {
    if (level >= this.logLevels.WARN) return false; // Don't deduplicate warnings and errors
    
    const cacheKey = this.generateCacheKey(level, message, data);
    const now = Date.now();
    
    if (this.logCache.has(cacheKey)) {
      const lastLog = this.logCache.get(cacheKey);
      if (now - lastLog.timestamp < this.cacheTimeout) {
        lastLog.count++;
        return true; // Should deduplicate
      }
    }
    
    // MEMORY FIX: Enforce size cap before adding new entry
    if (this.logCache.size >= this.maxLogCacheSize) {
      this.cleanupCache();
      // If still over limit after cleanup, evict oldest 50%
      if (this.logCache.size >= this.maxLogCacheSize) {
        const entries = Array.from(this.logCache.entries());
        entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
        const toRemove = Math.floor(entries.length / 2);
        for (let i = 0; i < toRemove; i++) {
          this.logCache.delete(entries[i][0]);
        }
      }
    }
    this.logCache.set(cacheKey, { timestamp: now, count: 1 });
    return false;
  }

  /**
   * Clean up expired cache entries
   */
  cleanupCache() {
    const now = Date.now();
    for (const [key, value] of this.logCache.entries()) {
      if (now - value.timestamp > this.cacheTimeout) {
        this.logCache.delete(key);
      }
    }
  }

  /**
   * Format log message with timestamp and level
   */
  formatMessage(level, message, data, component = '') {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    const levelStr = Object.keys(this.logLevels)[level];
    const componentStr = component ? `[${component}]` : '';
    
    return `${timestamp} ${levelStr} ${componentStr} ${message}`;
  }

  /**
   * Log error messages (always shown)
   */
  error(message, data = null, component = '') {
    if (!this.shouldLog(this.logLevels.ERROR)) return;
    
    const formattedMessage = this.formatMessage(this.logLevels.ERROR, message, data, component);
    console.error(formattedMessage, data || '');
  }

  /**
   * Log warning messages
   */
  warn(message, data = null, component = '') {
    if (!this.shouldLog(this.logLevels.WARN)) return;
    
    const formattedMessage = this.formatMessage(this.logLevels.WARN, message, data, component);
    console.warn(formattedMessage, data || '');
  }

  /**
   * Log info messages
   */
  info(message, data = null, component = '') {
    if (!this.shouldLog(this.logLevels.INFO)) return;
    
    const formattedMessage = this.formatMessage(this.logLevels.INFO, message, data, component);
    console.info(formattedMessage, data || '');
  }

  /**
   * Log debug messages (development only)
   */
  debug(message, data = null, component = '') {
    if (!this.shouldLog(this.logLevels.DEBUG)) return;
    
    const formattedMessage = this.formatMessage(this.logLevels.DEBUG, message, data, component);
    console.log(formattedMessage, data || '');
  }

  /**
   * Log trace messages (development only, with deduplication)
   */
  trace(message, data = null, component = '') {
    if (!this.shouldLog(this.logLevels.TRACE)) return;
    
    if (this.shouldDeduplicate(this.logLevels.TRACE, message, data)) {
      return; // Skip duplicate trace logs
    }
    
    const formattedMessage = this.formatMessage(this.logLevels.TRACE, message, data, component);
    console.log(formattedMessage, data || '');
  }

  /**
   * Performance logging for batch operations
   */
  performance(operation, duration, metadata = {}) {
    if (!this.shouldLog(this.logLevels.DEBUG)) return;
    
    const key = `${operation}_${JSON.stringify(metadata)}`;
    if (!this.performanceLogs.has(key)) {
      // MEMORY FIX: Enforce max keys cap
      if (this.performanceLogs.size >= this.maxPerformanceLogKeys) {
        // Evict oldest key
        const oldestKey = this.performanceLogs.keys().next().value;
        this.performanceLogs.delete(oldestKey);
      }
      this.performanceLogs.set(key, []);
    }
    
    this.performanceLogs.get(key).push({
      timestamp: Date.now(),
      duration,
      metadata
    });
  }

  /**
   * Flush performance logs in batches
   */
  flushPerformanceLogs() {
    for (const [key, logs] of this.performanceLogs.entries()) {
      if (logs.length >= this.batchSize) {
        const avgDuration = logs.reduce((sum, log) => sum + log.duration, 0) / logs.length;
        const minDuration = Math.min(...logs.map(log => log.duration));
        const maxDuration = Math.max(...logs.map(log => log.duration));
        
        this.debug(`Performance batch: ${key}`, {
          count: logs.length,
          avgDuration: avgDuration.toFixed(2),
          minDuration: minDuration.toFixed(2),
          maxDuration: maxDuration.toFixed(2),
          timeRange: `${logs[0].timestamp} - ${logs[logs.length - 1].timestamp}`
        }, 'Performance');
        
        this.performanceLogs.delete(key);
      }
    }
  }

  /**
   * Set log level dynamically
   */
  setLogLevel(level) {
    if (typeof level === 'string') {
      level = this.logLevels[level.toUpperCase()];
    }
    
    if (level !== undefined && level >= 0 && level <= 4) {
      this.currentLevel = level;
      this.info(`Log level changed to ${Object.keys(this.logLevels)[level]}`, null, 'Logger');
    }
  }

  /**
   * Get current log level
   */
  getLogLevel() {
    return Object.keys(this.logLevels)[this.currentLevel];
  }

  /**
   * Clear log cache
   */
  clearCache() {
    this.logCache.clear();
  }

  /**
   * Get log statistics
   */
  getStats() {
    return {
      currentLevel: this.getLogLevel(),
      cacheSize: this.logCache.size,
      performanceLogsCount: this.performanceLogs.size
    };
  }
}

// Create singleton instance
const logger = new Logger();

// Export both the class and instance
export { Logger, logger as default };
